/** @jsxImportSource @emotion/react */
import * as React from 'react';
import LabeledData from '../../../../forms/elements/LabeledData';
import { fullName } from '../../../module';
import Table from '../../../../layout/table/Table';
import { EntitlementShare } from '../../models';
import { GuestDetails } from '../../../guestDetails';
import { ColumnConfig } from '../../../../layout/table/columnConfig';
import { isNilOrEmpty, sortByDateProp, sortByProp} from '../../../../language/module';
import { Loader } from '../../../../forms/elements/loader/Loader';
import { css } from '@emotion/react';
import { apostrophize } from '../../../../language/strings';
import {useDispatch, useSelector} from 'react-redux';
import {copyToClipboardAction} from '../../../../copyToClipboard/copyToClipboardAction';
import {useCallback, useEffect} from 'react';
import {getGuestDetailsAction} from '../../../guestDetailsAction';
import {IAppState} from '../../../../core/redux/IAppState';
import {HistoricalRedemption} from '../historicalRedemption';
import {formatDate} from '../../../../language/time';
import Select from 'react-select';
import { getResource } from '../../../../language/resources/getResource';
import copy from '../../../../assets/icons/copyOutline.svg';
import { CLEAR_BUTTON, CLICKABLE, UNSELECTABLE_TEXT } from '../../../../styles/styles';


const FnFHistoryLoader = css`
  align-self: center;
  height: 120px;
  width: 120px;
  margin: 10px 0 !important; /* Fill space of form we're loading data for */
`
const allColumns        = css`word-wrap: break-word; hyphens: auto;`;
const externalCodeColumn = css`max-width: 800px; ${allColumns};`;

const toGuestColumn = css`
  max-width: 350px; 
  overflow: hidden; 
  text-overflow: ellipsis;
  cursor: pointer;
`;

const CopyBtn = css`
  width: 17px; 
  height: 17px; 
  margin-bottom: -2px; /* Align with text on the side */ 
`;

const CopyMgIdBtn = css`
    height: 20px;
    width: 25px;
    display: inline-block;
    margin-bottom: -2px; /* Align with text to the side */
    ${UNSELECTABLE_TEXT};
    ${CLEAR_BUTTON};
    ${CLICKABLE};
  `;

type Props = {
  guest: GuestDetails,
  entitlementShares: EntitlementShare[],
  isLoadingEntitlementShares: boolean,
  entitlementSharesError: boolean,
  redemptionHistory: HistoricalRedemption[],
}

/**
 * Displays the guest's entitlement share history and redemption history
 * */

const FnFHistoryTab = (props: Props) => {

  const dispatch = useDispatch();
  const guests = useSelector((state: IAppState) => {
    const guests = props.entitlementShares.reduce((acc : GuestDetails[], share) => {
      // Find guests from both to and from master guest ids to display full names in FNF share history
      const fromGuest = state.guests.get(share.fromMasterGuestId);
      const toGuest = state.guests.get(share.toMasterGuestId);
      if (fromGuest) {
        acc.push(fromGuest);
      }
      if (toGuest) {
        acc.push(toGuest);
      }
      return acc;
    }, []);
    return guests;
  });
  const resources = useSelector((state: IAppState) => state.locale);
  const dateFormat = useSelector((state: IAppState) => state.defaultDateFormat);
  const [isShareHistory, setIsShareHistory] = React.useState(true); // true is share history, false is redemption history
  const [hasGuestDetails, setHasGuestDetails] = React.useState(false);

  const options = [
    { value: 'Share', label: getResource(resources, 'FNF_HISTORY_SHARE') },
    { value: 'Redeem', label: getResource(resources, 'FNF_HISTORY_REDEEM') },
  ]
  
  const shareColumnConfig: Array<ColumnConfig<EntitlementShare>> = [
    { id: 1, tooltipText: getResource(resources, 'FNF_HISTORY_COLUMN_FROM_GUEST'), title: getResource(resources, 'FNF_HISTORY_COLUMN_FROM_GUEST'), onSort: sortByProp('fromMasterGuestId', true) },
    { id: 2, tooltipText: getResource(resources, 'FNF_HISTORY_COLUMN_TO_GUEST'), title: getResource(resources, 'FNF_HISTORY_COLUMN_TO_GUEST'), onSort: sortByProp('toMasterGuestId', true) },
    { id: 3, tooltipText: getResource(resources, 'FNF_HISTORY_COLUMN_SHARE_DATE'), title: getResource(resources, 'FNF_HISTORY_COLUMN_SHARE_DATE'), onSort: sortByDateProp('shareUtcDateTime'), defaultSortingColumn: true },
    { id: 4, tooltipText: getResource(resources, 'FNF_HISTORY_COLUMN_CLAIM_DATE'), title: getResource(resources, 'FNF_HISTORY_COLUMN_CLAIM_DATE'), onSort: sortByProp('claimedUtcDateTime', true) },
    { id: 5, tooltipText: getResource(resources, 'FNF_HISTORY_COLUMN_REVOKE_DATE'), title: getResource(resources, 'FNF_HISTORY_COLUMN_REVOKE_DATE'), onSort: sortByProp('revokedUtcDateTime', true) },
    { id: 6, tooltipText: getResource(resources, 'FNF_HISTORY_COLUMN_STATUS'), title: getResource(resources, 'FNF_HISTORY_COLUMN_STATUS'), onSort: sortByProp('entitlementShareStatusDescription', true) },
  ];
  
  // Use unique ids separate from the shareColumnConfig - otherwise, for some reason, the default sort column ends up being the sort column from the share table (since the share table is rendered first)
  const redemptionColumnConfig: Array<ColumnConfig<HistoricalRedemption>> = [
    { id: 11, tooltipText: getResource(resources, 'FNF_HISTORY_COLUMN_DATE'), title: getResource(resources, 'FNF_HISTORY_COLUMN_DATE'), onSort: sortByDateProp('redeemDate'), defaultSortingColumn: true },
    { id: 12, tooltipText: getResource(resources, 'FNF_HISTORY_COLUMN_RESORT'), title: getResource(resources, 'FNF_HISTORY_COLUMN_RESORT'), onSort: sortByProp('redeemResort', true) },
    { id: 13, tooltipText: '#', title: '#', onSort: sortByProp('redeemQuantity', true) },
    { id: 14, tooltipText: getResource(resources, 'FNF_HISTORY_COLUMN_REF_ID'), title: getResource(resources, 'FNF_HISTORY_COLUMN_REF_ID'), onSort: sortByProp('redemptionID', true) },
  ];

  const handleSelect = (event) => {
    setIsShareHistory(event.value === 'Share');
  };


  const copyMgid = useCallback((mgid: string) => () => {
    dispatch(copyToClipboardAction(mgid, getResource(resources, 'FNF_HISTORY_COPIED_MGID_TO_CLIPBOARD')));
  }, [dispatch, resources])

  // Take array of entitlement shares and get names for master guest ids
  useEffect(() => {
    if (props.entitlementShares.length > 0 && !hasGuestDetails) {
      const guestIdsToFetch = new Set<string>();

      props.entitlementShares.forEach((share) => {
        if (share.toMasterGuestId && !guests.some((guest) => guest.masterGuestId === share.toMasterGuestId)) {
          guestIdsToFetch.add(share.toMasterGuestId);
        }

        if (share.fromMasterGuestId && !guests.some((guest) => guest.masterGuestId === share.fromMasterGuestId)) {
          guestIdsToFetch.add(share.fromMasterGuestId);
        }
      });

      if (guestIdsToFetch.size > 0) {
        guestIdsToFetch.forEach((guestId) => {
          dispatch(getGuestDetailsAction(guestId));
        });

        setHasGuestDetails(true);
      }
    }
  }, [props.isLoadingEntitlementShares, props.entitlementShares, guests, dispatch, hasGuestDetails]);


  const matchGuestToShare = (mgid: string) => {
    return fullName(guests.find((guest) => guest && guest.masterGuestId === mgid));
  }


  const renderShareRow = (row: EntitlementShare): React.ReactNode => {
    return (
    <tr key={row.externalShareId}>
      <td onClick={copyMgid(row.fromMasterGuestId)} css={css`cursor: pointer`} title={row.fromMasterGuestId}>
        {row.fromMasterGuestId === props.guest.masterGuestId ? fullName(props.guest) : matchGuestToShare(row.fromMasterGuestId)}
        <button title={getResource(resources, 'FNF_HISTORY_COPY_MGID_TO_CLIPBOARD')} onClick={copyMgid(row.fromMasterGuestId)} css={CopyMgIdBtn}>
          <img src={copy} alt='Copy' css={CopyBtn} />
        </button>
      </td>
      <td onClick={copyMgid(row.toMasterGuestId)} css={toGuestColumn} title={row.toMasterGuestId ? row.toMasterGuestId : row.toEmailAddress}>
        { 
          row.toMasterGuestId ? 
            row.toMasterGuestId === props.guest.masterGuestId ? fullName(props.guest) : matchGuestToShare(row.toMasterGuestId) : 
            row.toEmailAddress 
        }
        {
          !isNilOrEmpty(row.toMasterGuestId) && (
          <button title={getResource(resources, 'FNF_HISTORY_COPY_MGID_TO_CLIPBOARD')} onClick={copyMgid(row.toMasterGuestId)} css={CopyMgIdBtn}>
            <img src={copy} alt='Copy' css={CopyBtn} />
          </button>)
        }
      </td>
      <td>{row.shareUtcDateTime ? formatDate(new Date(row.shareUtcDateTime), dateFormat) : null}</td>
      <td>{row.claimedUtcDateTime ? formatDate(new Date(row.claimedUtcDateTime), dateFormat) : null}</td>
      <td>{row.revokedUtcDateTime ?  formatDate(new Date(row.revokedUtcDateTime), dateFormat) : null}</td>
      <td>{row.entitlementShareStatusDescription}</td>
    </tr>
  )};

  const renderRedemptionRow = (row: HistoricalRedemption): React.ReactNode => (
    <tr key={row.redemptionID}>
      <td>
        {formatDate(row.redeemDate, dateFormat)}
      </td>
      <td>{row.redeemResort}</td>
      <td>{row.redeemQuantity}</td>
      <td css={externalCodeColumn}>{row.redemptionExternalCode}</td>
    </tr>
  );

  return (
    <>

      { // Loading entitlements
        props.isLoadingEntitlementShares
        && <Loader
          rootCss={FnFHistoryLoader}
        />
      }

      <LabeledData label={getResource(resources, 'FNF_HISTORY_GUEST_LABEL')} data={fullName(props.guest)}/>
      <Select options={options} onChange={handleSelect} defaultValue={options[0]} css={css`width: 30%; cursor: pointer`}/>
      {
        isShareHistory ?
          <Table columns={shareColumnConfig}
                 rootCss={css`min-width: 800px; max-height: 400px;`}
                 data={props.entitlementShares}
                 isLoading={props.isLoadingEntitlementShares}
                 noDataMessage={getResource(resources, 'FNF_HISTORY_NO_HISTORY')}
                 renderRow={renderShareRow}
                 error={
                   props.entitlementSharesError
                     ? getResource(resources, 'FNF_HISTORY_FAILED_FETCH', apostrophize(fullName(props.guest)))
                     : undefined
                 }
          />
          : <Table columns={ redemptionColumnConfig}
                   rootCss={css`min-width: 800px; max-height: 400px;`}
                   data={props.redemptionHistory}
                   isLoading={props.isLoadingEntitlementShares}
                   noDataMessage={getResource(resources, 'FNF_HISTORY_NO_HISTORY')}
                   renderRow={renderRedemptionRow}
                   error={
                     props.entitlementSharesError
                       ? getResource(resources, 'FNF_HISTORY_FAILED_FETCH', apostrophize(fullName(props.guest)))
                       : undefined
                   }
          />
      }

    </>
  )
}

export default FnFHistoryTab;
